const PIXEL_ID = '1051915437359917';
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

let initialized = false;

function getFbq(): FbqFunction | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  if (!window.fbq) {
    const fbq: FbqFunction = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue?.push(args);
      }
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    window.fbq = fbq;
    window._fbq = window._fbq || fbq;

    // Insert the Meta script into <head>. The official snippet inserts it before
    // the first <script> tag, which throws inside sandboxed previews that render
    // without any script element in the document.
    const script = document.createElement('script');
    script.async = true;
    script.src = SCRIPT_SRC;
    (document.head || document.documentElement).appendChild(script);
  }

  return window.fbq ?? null;
}

/**
 * Initializes the Meta (Facebook) Pixel and fires the initial PageView.
 * Safe to call multiple times — only sets up the pixel once.
 */
export function initMetaPixel() {
  if (initialized) return;
  const fbq = getFbq();
  if (!fbq) return;

  initialized = true;
  fbq('init', PIXEL_ID);
  fbq('track', 'PageView');
}

/**
 * Tracks a standard or custom Meta Pixel event (e.g. 'InitiateCheckout').
 * No-ops silently if the pixel isn't available.
 */
export function trackMetaPixelEvent(event: string, params?: Record<string, unknown>) {
  const fbq = typeof window !== 'undefined' ? window.fbq : undefined;
  if (!fbq) return;
  fbq('track', event, params);
}
